/**
 * The player's settings (the menu's Settings sheet), one small JSON blob beside the other
 * prefs: `arrow:v1:settings` = `{ "haptics", "sound", "clock", "largeClues", "shareBoard", "analytics", "theme" }`
 * (booleans, and the theme). Parsed
 * defensively: a bad or old value reads as the defaults, a missing field as its default,
 * and nothing here throws. It travels in the carry snapshot (pwa/carry.ts).
 *
 * - `haptics`: the light taps as you type and solve (game/haptics.ts). On by default.
 * - `sound`: small pops as you type and solve (game/sound.ts). On by default; a blob
 *   written before it existed reads as on.
 * - `clock`: the running clock in the game's masthead. Off hides it while you solve; the
 *   time is still kept, and the poster and Stats still show it.
 * - `largeClues`: the board's clue cells print at a higher floor (11px and up, not 8.5px);
 *   a clue that still can't fit shows a short cue ("Of the…") and reads in full in the
 *   clue bar (components/clueFit.ts). Off by default.
 * - `shareBoard`: the emoji board in share text. Off leaves it out (a screen reader reads
 *   it square by square); the stats line still says how the solve went. On by default.
 * - `analytics`: "Count my plays" (the privacy page). On by default; off, no analytics
 *   events are sent and the device isn't registered, and solves go to the api only when
 *   a challenge needs them (platform/consent.ts). Never itself tracked.
 * - `theme`: `system` (match the phone, the default), `light` or `dark` (docs/DARK-MODE.md).
 *   Applied to <html> as `data-theme` (app/theme.ts), and before the first paint by the
 *   inline script in index.html, which reads this same blob.
 *
 * Reduced motion follows the phone.
 */
import { useSyncExternalStore } from 'react';

import { browserStore, type KeyValueStore } from '../storage/kv.ts';

export type Theme = 'system' | 'light' | 'dark';
export const THEMES: readonly Theme[] = ['system', 'light', 'dark'];
export const isTheme = (value: unknown): value is Theme => typeof value === 'string' && (THEMES as readonly string[]).includes(value);

export interface Settings {
	haptics: boolean;
	sound: boolean;
	clock: boolean;
	largeClues: boolean;
	shareBoard: boolean;
	analytics: boolean;
	theme: Theme;
}
export type SettingKey = keyof Settings;
/** The on/off switches. */
export type SwitchKey = Exclude<SettingKey, 'theme'>;
export const SWITCH_KEYS: readonly SwitchKey[] = ['haptics', 'sound', 'clock', 'largeClues', 'shareBoard', 'analytics'];
/** The switches whose changes are tracked (`settings_changed`): all but the analytics choice itself. */
export type TrackedSwitch = Exclude<SwitchKey, 'analytics'>;
export const SETTING_KEYS: readonly SettingKey[] = [...SWITCH_KEYS, 'theme'];

export const SETTINGS_KEY = 'arrow:v1:settings';
export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({
	haptics: true,
	sound: true,
	clock: true,
	largeClues: false,
	shareBoard: true,
	analytics: true,
	theme: 'system',
});

/** Is `value` a valid value for this setting? */
function valid(key: SettingKey, value: unknown): boolean {
	return key === 'theme' ? isTheme(value) : typeof value === 'boolean';
}

/** A stored value as settings: anything unreadable is the default. Never throws. */
export function parseSettings(raw: string | null | undefined): Settings {
	const out: Settings = { ...DEFAULT_SETTINGS };
	if (!raw) return out;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return out;
	}
	if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out;
	const o = parsed as Record<string, unknown>;
	for (const key of SWITCH_KEYS) if (typeof o[key] === 'boolean') out[key] = o[key];
	if (isTheme(o.theme)) out.theme = o.theme;
	return out;
}

export interface SettingsStore {
	get(): Settings;
	set<K extends SettingKey>(key: K, value: Settings[K]): void;
	subscribe(fn: () => void): () => void;
}

export function createSettings(store: KeyValueStore = browserStore()): SettingsStore {
	let current: Settings | null = null;
	const listeners = new Set<() => void>();
	const get = (): Settings => {
		current ??= parseSettings(store.get(SETTINGS_KEY));
		return current;
	};
	return {
		get,
		set(key, value) {
			if (!SETTING_KEYS.includes(key) || !valid(key, value)) return;
			current = { ...get(), [key]: value };
			store.set(SETTINGS_KEY, JSON.stringify(current));
			for (const fn of [...listeners]) fn();
		},
		subscribe(fn) {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
	};
}

let shared: SettingsStore | null = null;
/** The app's settings (localStorage). */
export function settings(): SettingsStore {
	shared ??= createSettings();
	return shared;
}

/** Tests: forget the cached settings (after clearing storage). */
export function resetSettings(): void {
	shared = null;
}

/** The settings, re-rendering when one changes. */
export function useSettings(): Settings {
	const s = settings();
	return useSyncExternalStore(s.subscribe, s.get, s.get);
}
